import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { createNotification } from '../../services/NotificationSystem';
import { Globe, RefreshCw, CheckCircle, XCircle, Languages, FolderOpen, Printer } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { ToothChart, toothChartHtml, shadesOf, groupDetails, materialsOf } from './ToothChart';

// 🌏 국내 기공소용 글로벌 수주 화면 (한글) — 해외 발주 케이스 의뢰서 확인·수락·상태 관리
// 파일은 에이전트가 PC의 받은케이스 폴더에 자동 저장 (웹은 의뢰서·상태만)
const STATUS = {
    queued: { label: '파일 전송 중', bg: '#EEF0FA', color: '#4F52C7' },
    sent: { label: '신규 의뢰', bg: '#FDF3E3', color: '#B45309' },
    accepted: { label: '수락됨', bg: '#E6F5F1', color: '#0B6E5D' },
    working: { label: '작업 중', bg: '#E6F5F1', color: '#0B6E5D' },
    returned: { label: '회신 완료', bg: '#EEF0FA', color: '#4F52C7' },
    confirmed: { label: '확정 (청구 대상)', bg: '#E6F5F1', color: '#0B6E5D' },
    revision: { label: '수정 요청받음', bg: '#FEF2F2', color: '#DC2626' },
    declined: { label: '거절함', bg: '#F1F5F9', color: '#64748B' },
    canceled: { label: '취소됨', bg: '#F1F5F9', color: '#64748B' },
};

function GlobalLabOrders({ user }) {
    const myUid = user?.companyId || user?.uid;
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [translating, setTranslating] = useState(null);
    const [sheetFor, setSheetFor] = useState(null);   // 의뢰서 모달

    const load = () => {};   // 실시간 구독으로 대체 (버튼 호환용 no-op)
    useEffect(() => {
        if (!myUid) return;
        setLoading(true);
        const unsub = onSnapshot(query(collection(db, 'outsourceOrders'), where('toUserId', '==', myUid)), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
                .filter(o => o.source === 'agent_auto' && o.caseNo);
            list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setOrders(list);
            setLoading(false);
        }, () => setLoading(false));
        return () => unsub();
        // eslint-disable-next-line
    }, [myUid]);

    // 상태 전이 시 해외 발주자에게 보낼 영문 웹 알림 문구 (returned에는 3일 자동확정 경고 포함)
    const buyerNotice = (o, status) => {
        const lab = o.toUserName || 'the lab';
        const days = o.autoConfirmDays || 3;
        switch (status) {
            case 'accepted': return { title: 'Case accepted', message: `Your case ${o.caseNo} was accepted by ${lab}.` };
            case 'working': return { title: 'Case in progress', message: `Your case ${o.caseNo} is now in progress at ${lab}.` };
            case 'returned': return { title: 'Design returned', message: `Design for ${o.caseNo} was returned — please review within ${days} days. It will be auto-confirmed for invoicing if no action is taken.` };
            case 'declined': return { title: 'Case declined', message: `Your case ${o.caseNo} was declined by ${lab}. Please contact the lab or choose another lab.` };
            default: return null;
        }
    };

    const setStatus = async (o, status, confirmMsg) => {
        if (confirmMsg && !window.confirm(confirmMsg)) return;
        setBusyId(o.id);
        try {
            await updateDoc(doc(db, 'outsourceOrders', o.id), { status, updatedAt: new Date() });
            // 해외 발주자(fromUserId) 웹 알림함에 영문 알림 생성 — 실패해도 상태 변경은 유지
            const notice = buyerNotice(o, status);
            if (notice && o.fromUserId) {
                try {
                    await createNotification({
                        recipientId: o.fromUserId,
                        type: 'global_case_status',
                        title: notice.title,
                        message: notice.message,
                        link: '/global/orders',
                        metadata: { caseNo: o.caseNo, orderId: o.id, status },
                    });
                } catch (e) { /* 알림 실패는 무시 */ }
            }
            load();
        } catch (e) { alert('처리에 실패했습니다.'); }
        finally { setBusyId(null); }
    };

    // 메모·수정사유 한국어 번역 (캐시: requestNoteKo / revisions 번역은 화면 표시만)
    const translateNote = async (o) => {
        setTranslating(o.id);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'translateText');
            const res = await fn({ text: o.requestNote, targetLang: 'ko' });
            const ko = res?.data?.translated || '';
            if (ko) await updateDoc(doc(db, 'outsourceOrders', o.id), { requestNoteKo: ko });
            load();
        } catch (e) { alert('번역에 실패했습니다.'); }
        finally { setTranslating(null); }
    };

    // 수정 요청 사유 번역 (회차별 캐시: revisionsKo.{rev})
    const translateRevision = async (o, revision) => {
        setTranslating(o.id);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'translateText');
            const res = await fn({ text: revision.reason, targetLang: 'ko' });
            const ko = res?.data?.translated || '';
            if (ko) await updateDoc(doc(db, 'outsourceOrders', o.id), { [`revisionsKo.${revision.rev}`]: ko });
            load();
        } catch (e) { alert('번역에 실패했습니다.'); }
        finally { setTranslating(null); }
    };

    const fmt = (v) => v?.toDate ? v.toDate().toLocaleDateString('ko-KR') : '-';
    const newCount = orders.filter(o => o.status === 'sent').length;

    // 의뢰서 인쇄/PDF — 인쇄용 시트를 새 창에 열고 print() (대화상자에서 프린트 또는 PDF 저장)
    const openOrderSheet = (o) => {
        const st = STATUS[o.status] || STATUS.sent;
        const details = [...(o.teethDetails || [])].sort((a, b) => (parseInt(a.tooth, 10) || 99) - (parseInt(b.tooth, 10) || 99));
        const groups = groupDetails(details);
        const rows = groups.length
            ? groups.map(g => `<tr>
                <td style="font-weight:700">${g.teeth.join(', ')} <span style="color:#94a3b8;font-weight:400">(${g.teeth.length}개)</span></td>
                <td>${g.isPontic ? '폰틱' : (g.prosthesis || o.indication || '-')}</td>
                <td>${g.material || o.bundles?.[0]?.implantType || '-'}</td><td>${g.shade || '-'}</td>
                <td>${[g.implant && ('임플란트: ' + g.implant), g.isBridge && '브릿지'].filter(Boolean).join(' · ')}</td>
              </tr>`).join('')
            : `<tr><td colspan="5" style="color:#94a3b8">치식 상세 없음 — 파일 참조</td></tr>`;
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>의뢰서 ${o.caseNo}</title>
<style>
  body{font-family:-apple-system,'Malgun Gothic',sans-serif;color:#1e2230;margin:40px;font-size:14px}
  h1{font-size:20px;margin:0 0 2px} .sub{color:#64748b;font-size:12.5px;margin-bottom:20px}
  .meta{display:grid;grid-template-columns:repeat(2,1fr);gap:6px 24px;border:1px solid #e2e8f0;border-radius:10px;padding:14px 18px;margin-bottom:18px}
  .meta b{display:inline-block;min-width:70px;color:#64748b;font-weight:600}
  table{width:100%;border-collapse:collapse;margin-bottom:18px}
  th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left;font-size:13px}
  th{background:#f8fafc;color:#64748b;font-size:12px}
  .note{border:1px solid #e2e8f0;border-radius:10px;padding:12px 16px;line-height:1.7;margin-bottom:14px}
  .foot{color:#94a3b8;font-size:11.5px;margin-top:24px;border-top:1px solid #e2e8f0;padding-top:10px}
  @media print{ body{margin:14mm} }
</style></head><body>
  <h1>글로벌 디자인 의뢰서 <span style="font-family:ui-monospace,monospace">${o.caseNo}</span></h1>
  <div class="sub">DentCluster Global · 상태: ${st.label}${(o.designRev || 0) > 0 ? ` · r${o.designRev}` : ''}</div>
  <div class="meta">
    <div><b>발주처</b> ${o.fromUserName || '해외 발주처'}</div>
    <div><b>수주처</b> ${o.toUserName || ''}</div>
    <div><b>접수일</b> ${fmt(o.createdAt)}</div>
    <div><b>납기일</b> ${fmt(o.dueDate)}</div>
    ${o.patientRef ? `<div><b>환자참조</b> ${o.patientRef}</div>` : ''}
    <div><b>보철</b> ${o.indication || '-'}</div>
    <div><b>재료</b> ${materialsOf(o.teethDetails, o.bundles?.[0]?.implantType)}</div>
  </div>
  <div style="display:flex;align-items:center;gap:22px;margin-bottom:16px;flex-wrap:wrap">${toothChartHtml(details.map(d => d.tooth))}${shadesOf(details).length ? `<div style="font-size:14px"><b>&lt;쉐이드&gt;</b> ${shadesOf(details).join(', ')}</div>` : ''}</div>
  <table>
    <thead><tr><th>치식</th><th>보철</th><th>재료</th><th>쉐이드</th><th>비고</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  ${o.requestNote ? `<div class="note"><b>요청 메모</b><br/>${(o.requestNoteKo || o.requestNote).replace(/\n/g, '<br/>')}${o.requestNoteKo ? `<div style="color:#94a3b8;font-size:12px;margin-top:6px">원문: ${o.requestNote.replace(/\n/g, '<br/>')}</div>` : ''}</div>` : ''}
  <div class="note" style="background:#f8fafc"><b>파일 위치</b><br/>문서/DentCluster/Global/받은케이스/${o.fromUserName || ''}/${o.caseNo}/<br/>완성물은 케이스 폴더 안 <b>_return</b>에 저장하면 자동 회신됩니다.</div>
  <div class="foot">환자 식별정보는 개인정보 보호를 위해 케이스번호로 치환되어 있습니다 · 출력일 ${new Date().toLocaleDateString('ko-KR')}</div>
<script>window.onload=function(){window.print()}</script></body></html>`;
        const w = window.open('', '_blank');
        if (!w) { alert('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.'); return; }
        w.document.write(html);
        w.document.close();
    };

    return (
        <div style={{ maxWidth: 940, margin: '0 auto', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <PageHeader icon={Globe} title="글로벌 수주" />
                <button onClick={load} style={{ border: '1px solid #E4E6EE', background: '#fff', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: '#5A6072', display: 'inline-flex', alignItems: 'center', gap: 5 }}><RefreshCw size={13} /> 새로고침</button>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#EEF0FA', border: '1px solid #C7D2FE', borderRadius: 10, padding: '12px 16px', margin: '10px 0 16px', fontSize: 13, color: '#3730A3', lineHeight: 1.7 }}>
                <FolderOpen size={15} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                    파일은 이 PC의 에이전트가 <b>문서/DentCluster/Global/받은케이스/{'{발주처}'}/{'{케이스번호}'}/</b> 폴더에 자동 저장합니다.
                    작업 후 완성물을 케이스 폴더 안 <b>_return</b> 폴더에 넣으면 자동 회신되고, 아래 상태도 자동으로 "회신 완료"로 바뀝니다.
                    확정(청구 대상) 건은 월마감 때 외주명세서에서 집계됩니다.
                </span>
            </div>

            {loading ? <div style={{ color: '#5A6072', padding: 30 }}>불러오는 중…</div> : orders.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94A3B8', padding: '50px 0', background: '#fff', border: '1px solid #E4E6EE', borderRadius: 12 }}>
                    아직 해외 수주 케이스가 없습니다.
                </div>
            ) : (
                <>
                    {newCount > 0 && <div style={{ fontSize: 13, fontWeight: 800, color: '#B45309', marginBottom: 8 }}>신규 의뢰 {newCount}건 — 확인 후 수락해주세요</div>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {orders.map(o => {
                            const st = STATUS[o.status] || STATUS.sent;
                            const teeth = (o.teethDetails || []).map(t => t.tooth).filter(Boolean).join(',');
                            const lastRevision = (o.revisions || [])[Math.max(0, (o.revisions || []).length - 1)];
                            return (
                                <div key={o.id} onClick={() => setSheetFor(o)}
                                    style={{ background: '#fff', border: '1px solid #E4E6EE', borderRadius: 12, padding: '14px 18px', cursor: 'pointer' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                        <div>
                                            <span style={{ fontWeight: 800, fontFamily: 'ui-monospace, monospace', fontSize: 15 }}>{o.caseNo}</span>
                                            {o.patientRef && <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 700, color: '#1E2230' }}>({o.patientRef})</span>}
                                            <span style={{ marginLeft: 10, fontSize: 13.5, color: '#1E2230', fontWeight: 700 }}>{o.fromUserName || '해외 발주처'}</span>
                                            <span style={{ marginLeft: 10, fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: st.bg, color: st.color }}>{st.label}</span>
                                            {(o.designRev || 0) > 0 && <span style={{ marginLeft: 6, fontSize: 11.5, color: '#94A3B8' }}>r{o.designRev}</span>}
                                        </div>
                                        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                                            <button onClick={() => openOrderSheet(o)}
                                                style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #E4E6EE', background: '#fff', color: '#5A6072', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                <Printer size={13} /> 의뢰서
                                            </button>
                                            {o.status === 'sent' && (
                                                <>
                                                    <button disabled={busyId === o.id} onClick={() => setStatus(o, 'accepted')}
                                                        style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#0E8A74', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                        <CheckCircle size={13} /> 수락
                                                    </button>
                                                    <button disabled={busyId === o.id} onClick={() => setStatus(o, 'declined', '이 케이스를 거절할까요? 발주처에 거절 상태로 표시됩니다.')}
                                                        style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #DC2626', background: '#fff', color: '#DC2626', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                        <XCircle size={13} /> 거절
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 8, fontSize: 13, color: '#5A6072', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                        <span>치식 <b>{teeth || '-'}</b></span>
                                        {o.indication && <span>보철 <b>{o.indication}</b></span>}
                                        <span>재료 <b>{materialsOf(o.teethDetails, o.bundles?.[0]?.implantType)}</b></span>
                                        <span>납기 <b>{fmt(o.dueDate)}</b></span>
                                        <span style={{ color: '#94A3B8' }}>접수 {fmt(o.createdAt)}</span>
                                    </div>
                                    {o.requestNote && (
                                        <div style={{ marginTop: 8, padding: '9px 12px', background: '#F8FAFC', borderRadius: 8, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                                            <b>요청 메모:</b> {o.requestNoteKo || o.requestNote}
                                            {!o.requestNoteKo && (
                                                <button onClick={(e) => { e.stopPropagation(); translateNote(o); }} disabled={translating === o.id}
                                                    style={{ marginLeft: 8, border: 'none', background: 'none', cursor: 'pointer', color: '#4F52C7', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0 }}>
                                                    <Languages size={12} /> {translating === o.id ? '번역 중…' : '번역'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {o.status === 'revision' && lastRevision?.reason && (
                                        <div style={{ marginTop: 8, padding: '9px 12px', background: '#FEF2F2', borderRadius: 8, fontSize: 13, color: '#B91C1C', lineHeight: 1.6 }}>
                                            <b>수정 요청 (r{lastRevision.rev}):</b> {o.revisionsKo?.[lastRevision.rev] || lastRevision.reason}
                                            {!o.revisionsKo?.[lastRevision.rev] && (
                                                <button onClick={(e) => { e.stopPropagation(); translateRevision(o, lastRevision); }} disabled={translating === o.id}
                                                    style={{ marginLeft: 8, border: 'none', background: 'none', cursor: 'pointer', color: '#DC2626', fontSize: 12, fontWeight: 700, padding: 0 }}>
                                                    🌐 {translating === o.id ? '번역 중…' : '번역'}
                                                </button>
                                            )}
                                            <span style={{ display: 'block', fontSize: 12, color: '#DC2626', marginTop: 2 }}>수정 후 같은 케이스 폴더의 _return에 다시 넣으면(같은 파일명 덮어쓰기 가능) 자동 회신됩니다.</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* 의뢰서 모달 — 카드 클릭 시 */}
            {sheetFor && (() => {
                const o = sheetFor;
                const st = STATUS[o.status] || STATUS.sent;
                const details = [...(o.teethDetails || [])].sort((a, b) => (parseInt(a.tooth, 10) || 99) - (parseInt(b.tooth, 10) || 99));
                return (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }} onClick={() => setSheetFor(null)}>
                        <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', padding: '22px 24px' }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                <div>
                                    <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'ui-monospace, monospace' }}>{o.caseNo}</div>
                                    <div style={{ fontSize: 12.5, color: '#5A6072', marginTop: 2 }}>
                                        글로벌 디자인 의뢰서 · <span style={{ fontWeight: 700, color: st.color }}>{st.label}</span>{(o.designRev || 0) > 0 ? ` · r${o.designRev}` : ''}
                                        {o.patientRef && <span style={{ marginLeft: 8 }}>환자참조 {o.patientRef}</span>}
                                    </div>
                                </div>
                                <button onClick={() => setSheetFor(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 18 }}>✕</button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 18px', border: '1px solid #E4E6EE', borderRadius: 10, padding: '12px 16px', margin: '12px 0', fontSize: 13.5 }}>
                                <div><span style={S2.k}>발주처</span> {o.fromUserName || '해외 발주처'}</div>
                                <div><span style={S2.k}>수주처</span> {o.toUserName || ''}</div>
                                <div><span style={S2.k}>접수일</span> {fmt(o.createdAt)}</div>
                                <div><span style={S2.k}>납기일</span> <b>{fmt(o.dueDate)}</b></div>
                                <div><span style={S2.k}>보철</span> {o.indication || '-'}</div>
                                <div><span style={S2.k}>재료</span> {materialsOf(o.teethDetails, o.bundles?.[0]?.implantType)}</div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
                                <ToothChart teeth={details.map(d => d.tooth)} />
                                {shadesOf(details).length > 0 && <div style={{ fontSize: 13.5 }}><b>&lt;쉐이드&gt;</b> {shadesOf(details).join(', ')}</div>}
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
                                <thead><tr>
                                    {['치식', '보철', '재료', '쉐이드', '비고'].map(h =>
                                        <th key={h} style={S2.th}>{h}</th>)}
                                </tr></thead>
                                <tbody>
                                    {details.length === 0 ? (
                                        <tr><td colSpan={5} style={{ ...S2.td, color: '#94A3B8' }}>치식 상세 없음 — 파일 참조</td></tr>
                                    ) : groupDetails(details).map((g, i) => (
                                        <tr key={i}>
                                            <td style={{ ...S2.td, fontWeight: 700 }}>{g.teeth.join(', ')} <span style={{ color: '#94A3B8', fontWeight: 400 }}>({g.teeth.length}개)</span></td>
                                            <td style={S2.td}>{g.isPontic ? '폰틱' : (g.prosthesis || o.indication || '-')}</td>
                                            <td style={S2.td}>{g.material || o.bundles?.[0]?.implantType || '-'}</td>
                                            <td style={S2.td}>{g.shade || '-'}</td>
                                            <td style={S2.td}>{[g.implant && `임플란트: ${g.implant}`, g.isBridge && '브릿지'].filter(Boolean).join(' · ')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {o.requestNote && (
                                <div style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 10 }}>
                                    <b>요청 메모:</b> {o.requestNoteKo || o.requestNote}
                                    {!o.requestNoteKo && (
                                        <button onClick={() => translateNote(o)} disabled={translating === o.id}
                                            style={{ marginLeft: 8, border: 'none', background: 'none', cursor: 'pointer', color: '#4F52C7', fontSize: 12, fontWeight: 700, padding: 0 }}>
                                            🌐 {translating === o.id ? '번역 중…' : '번역'}
                                        </button>
                                    )}
                                </div>
                            )}
                            <div style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#64748B', lineHeight: 1.6, marginBottom: 14 }}>
                                파일: 문서/DentCluster/Global/받은케이스/{o.fromUserName || ''}/{o.caseNo}/ · 완성물은 <b>_return</b>에 저장하면 자동 회신
                            </div>

                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                {o.status === 'sent' && (
                                    <>
                                        <button disabled={busyId === o.id} onClick={() => { setStatus(o, 'accepted'); setSheetFor(null); }}
                                            style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#0E8A74', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>✓ 수락</button>
                                        <button disabled={busyId === o.id} onClick={() => { setStatus(o, 'declined', '이 케이스를 거절할까요?'); setSheetFor(null); }}
                                            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #DC2626', background: '#fff', color: '#DC2626', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>거절</button>
                                    </>
                                )}
                                <button onClick={() => openOrderSheet(o)}
                                    style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #E4E6EE', background: '#fff', color: '#5A6072', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                    <Printer size={14} /> 인쇄 / PDF 저장
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

const S2 = {
    k: { display: 'inline-block', minWidth: 52, color: '#94A3B8', fontSize: 12.5 },
    th: { border: '1px solid #E4E6EE', padding: '7px 10px', textAlign: 'left', background: '#F8FAFC', color: '#64748B', fontSize: 12 },
    td: { border: '1px solid #E4E6EE', padding: '7px 10px' },
};

export default GlobalLabOrders;
